/**
 * /admin/knowledge — the tenant's Knowledge Hub (Knowledge_Hub_Design.md 4.2, 12 Phase 1d).
 *
 * Tenant-shared documents (regulations, enterprise standards, glossary) that
 * every project in the tenant can see, curated by a `knowledge:manage_tenant`
 * holder; the ZTD-provided packs (read-only); and the record of documents
 * copied from projects to tenant-shared. Project-private documents are not
 * managed here — each project's Knowledge Hub section lists its own.
 */
import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Trash2,
  Upload,
} from "lucide-react";
import { useAuth } from "../../auth";
import {
  hubDocumentId,
  knowledgeHub,
  TENANT_DOC_TYPES,
  type KnowledgeItem,
  type PromotionRecord,
} from "../../api/knowledgeHub";

interface Props {
  onBack: () => void;
}

const CARD = {
  background: "rgba(28,28,42,0.5)",
  border: "1px solid rgba(255,255,255,0.05)",
};

export default function KnowledgeHubAdmin({ onBack }: Props) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("knowledge:manage_tenant");
  const canSeePromotions = hasPermission("knowledge:promote");
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [promotions, setPromotions] = useState<PromotionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      setItems(await knowledgeHub.catalog("tenant"));
      if (canSeePromotions) setPromotions(await knowledgeHub.promotions());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [canSeePromotions]);

  useEffect(() => {
    void load();
  }, [load]);

  const tenantDocs = items.filter((i) => i.visibility === "tenant_shared");
  const packs = items.filter((i) => i.kind === "pack");
  const unavailable = items.filter((i) => i.kind === "unavailable");

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setError("");
    try {
      await fn();
      setNotice(label);
      setTimeout(() => setNotice(""), 3000);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    }
  };

  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-white/30 hover:text-brand-500 text-xs transition"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
            <BookOpen size={20} className="text-brand-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Knowledge Hub</h1>
            <p className="text-xs text-white/30">
              Tenant-shared knowledge every project can use, and the
              ZTD-provided packs
            </p>
          </div>
        </div>

        {error && (
          <div
            className="flex items-center gap-2 text-xs text-red-400"
            data-testid="kh-admin-error"
          >
            <AlertTriangle size={13} /> {error}
          </div>
        )}
        {notice && (
          <div className="text-xs text-green-400" data-testid="kh-admin-notice">
            {notice}
          </div>
        )}
        {unavailable.length > 0 && (
          <div className="text-xs text-amber-400">
            Could not list: {unavailable.map((u) => u.title).join(", ")}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-white/30" />
          </div>
        ) : (
          <>
            {canManage && (
              <UploadCard
                onUploaded={(doc) =>
                  load().then(() =>
                    setNotice(
                      doc.status === "pending_classification_review"
                        ? "Uploaded — held for Tier-3 classification review"
                        : `Uploaded — ${doc.status}`,
                    ),
                  )
                }
                onError={setError}
              />
            )}

            <section
              className="glass rounded-2xl p-5"
              style={CARD}
              data-testid="kh-tenant-docs"
            >
              <h2 className="text-sm font-bold text-white mb-1">
                Tenant-shared documents ({tenantDocs.length})
              </h2>
              <p className="text-xs text-white/40 mb-3">
                Visible to every project in this tenant. A project includes a
                document for its agents in its own Knowledge Hub section;
                &quot;Default on&quot; includes it in projects created from now
                on (existing projects are unchanged).
              </p>
              {tenantDocs.length === 0 ? (
                <p className="text-xs text-white/25 italic">None yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-white/40">
                      <th className="py-1.5">Document</th>
                      <th>Status</th>
                      <th>Size</th>
                      <th>Default on</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {tenantDocs.map((d) => {
                      const id = hubDocumentId(d.ref);
                      const held = d.status === "pending_classification_review";
                      return (
                        <tr
                          key={d.ref}
                          className="border-t border-white/5"
                          data-testid="kh-tenant-doc-row"
                        >
                          <td className="py-2">
                            <div className="text-white/80">{d.title}</div>
                            <div className="text-[11px] text-white/30">
                              {d.doc_type}
                              {d.details.promoted_from_ref
                                ? " · copied from a project"
                                : ""}
                            </div>
                          </td>
                          <td>
                            <StatusBadge item={d} />
                          </td>
                          <td className="text-white/50">
                            ≈ {d.size_estimate_tokens} tokens ·{" "}
                            {d.chunk_count ?? 0} chunk
                            {d.chunk_count === 1 ? "" : "s"}
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!d.default_on_since}
                              disabled={!canManage}
                              aria-label={`Default on: ${d.title}`}
                              data-testid="kh-default-on"
                              onChange={(e) =>
                                act("Saved", () =>
                                  knowledgeHub.setDefaultOn(
                                    id,
                                    e.target.checked,
                                  ),
                                )
                              }
                              className="accent-brand-500"
                            />
                          </td>
                          <td className="text-right space-x-2 whitespace-nowrap">
                            {canManage && held && (
                              <button
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Clear the Tier-3 hold on "${d.title}"? Its redacted text becomes available to every project in this tenant. (You cannot clear a document you uploaded yourself.)`,
                                    )
                                  )
                                    void act("Cleared and processed", () =>
                                      knowledgeHub.clearTenantDocumentReview(
                                        id,
                                      ),
                                    );
                                }}
                                className="text-amber-300 hover:text-amber-200"
                                data-testid="kh-clear-review"
                              >
                                <ShieldAlert size={12} className="inline" />{" "}
                                Clear review
                              </button>
                            )}
                            {canManage && !held && (
                              <button
                                onClick={() =>
                                  act("Re-indexed", () =>
                                    knowledgeHub.reindexTenantDocument(id),
                                  )
                                }
                                className="text-white/40 hover:text-white"
                                title="Re-extract and re-chunk from the stored file"
                                data-testid="kh-reindex"
                              >
                                <RefreshCw size={12} className="inline" />
                              </button>
                            )}
                            {canManage && (
                              <button
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Delete "${d.title}" for every project in this tenant?`,
                                    )
                                  )
                                    void act("Deleted", () =>
                                      knowledgeHub.deleteTenantDocument(id),
                                    );
                                }}
                                className="text-red-400/70 hover:text-red-400"
                                aria-label={`Delete ${d.title}`}
                                data-testid="kh-delete"
                              >
                                <Trash2 size={12} className="inline" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>

            <section
              className="glass rounded-2xl p-5"
              style={CARD}
              data-testid="kh-packs"
            >
              <h2 className="text-sm font-bold text-white mb-1">
                ZTD-provided packs ({packs.length})
              </h2>
              <p className="text-xs text-white/40 mb-3">
                Shipped with ZTD and changed only by code review. Engagement DQ
                packs are chosen per project by its Data Steward.
              </p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-white/40">
                    <th className="py-1.5">Pack</th>
                    <th>Version</th>
                    <th>Kind</th>
                    <th>Used by</th>
                  </tr>
                </thead>
                <tbody>
                  {packs.map((p) => (
                    <tr
                      key={p.ref}
                      className="border-t border-white/5"
                      data-testid="kh-pack-row"
                    >
                      <td className="py-1.5 font-mono text-white/70">
                        {p.title}
                      </td>
                      <td className="text-white/50">{p.version ?? "—"}</td>
                      <td className="text-white/50">{p.knowledge_class}</td>
                      <td className="text-white/50">
                        {p.doc_type === "dq_rule_pack"
                          ? `${String(p.details.rule_count ?? 0)} rules · engagement ${String(p.details.engagement_slug)}`
                          : p.managed_by}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {canSeePromotions && (
              <section
                className="glass rounded-2xl p-5"
                style={CARD}
                data-testid="kh-promotions"
              >
                <h2 className="text-sm font-bold text-white mb-3">
                  Copied from projects ({promotions.length})
                </h2>
                {promotions.length === 0 ? (
                  <p className="text-xs text-white/25 italic">None.</p>
                ) : (
                  <ul className="space-y-2 text-xs">
                    {promotions.map((p) => (
                      <li
                        key={p.id}
                        className="text-white/60"
                        data-testid="kh-promotion-row"
                      >
                        <div>
                          <span className="text-white/80">
                            {p.target_title ?? "(copy deleted)"}
                          </span>{" "}
                          <span className="text-white/40">
                            · {new Date(p.promoted_at).toLocaleString()} · by{" "}
                            {p.promoted_by} · from project{" "}
                            <span className="font-mono">
                              {p.source_project_id}
                            </span>
                          </span>
                        </div>
                        <div className="text-white/50">{p.justification}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ item }: { item: KnowledgeItem }) {
  if (item.status === "ready")
    return <span className="ztd-badge ztd-badge-success">Ready</span>;
  if (item.status === "pending_classification_review")
    return (
      <span className="ztd-badge ztd-badge-warning">Needs review (Tier 3)</span>
    );
  if (item.status === "processing")
    return <span className="ztd-badge ztd-badge-info">Processing</span>;
  return (
    <span
      className="ztd-badge ztd-badge-critical"
      title={String(item.details.error_detail ?? "")}
    >
      Failed
    </span>
  );
}

function UploadCard({
  onUploaded,
  onError,
}: {
  onUploaded: (doc: KnowledgeItem) => void;
  onError: (message: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState("regulation");
  const [title, setTitle] = useState("");
  const [defaultOn, setDefaultOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const doc = await knowledgeHub.uploadTenantDocument(
        file,
        docType,
        title,
        defaultOn,
      );
      setFile(null);
      setTitle("");
      onUploaded(doc);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="glass rounded-2xl p-5 space-y-3"
      style={CARD}
      data-testid="kh-upload"
    >
      <h2 className="text-sm font-bold text-white">
        Add a tenant-shared document
      </h2>
      <p className="text-xs text-white/40">
        Scanned for malware, checked for Tier-3 data (a hit holds it for review)
        and redacted before it is stored. Never a client&apos;s project
        document: those stay in their project.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          type="file"
          accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          data-testid="kh-upload-file"
          className="text-xs text-white/60"
        />
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          data-testid="kh-upload-type"
          className="px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white"
        >
          {TENANT_DOC_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional)"
          maxLength={300}
          data-testid="kh-upload-title"
          className="px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20"
        />
        <label className="flex items-center gap-2 text-xs text-white/60">
          <input
            type="checkbox"
            checked={defaultOn}
            onChange={(e) => setDefaultOn(e.target.checked)}
            data-testid="kh-upload-default-on"
            className="accent-brand-500"
          />
          Default on for projects created from now on
        </label>
      </div>
      <button
        onClick={upload}
        disabled={!file || busy}
        data-testid="kh-upload-submit"
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40"
        style={{ background: "linear-gradient(135deg, #86BC25, #6B9B1E)" }}
      >
        {busy ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <Upload size={14} />
        )}
        Upload
      </button>
    </section>
  );
}
